import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

// Map i18n locale codes to BCP 47 language tags for toLocaleDateString
const localeMap: Record<string, string> = {
  th: 'th-TH',
  zh: 'zh-TW',
  en: 'en-US'
}

export interface DateFormatOptions {
  weekday?: 'long' | 'short' | 'narrow'
  year?: 'numeric' | '2-digit'
  month?: 'long' | 'short' | 'narrow' | 'numeric' | '2-digit'
  day?: 'numeric' | '2-digit'
}

export function useDateFormatter() {
  const { locale } = useI18n()

  const browserLocale = computed(() => {
    return localeMap[locale.value] || 'en-US'
  })

  /**
   * Format a date string (YYYY-MM-DD) to localized display string
   * Uses local time to avoid timezone shifts
   */
  function formatDate(
    dateStr: string | Date | null | undefined,
    options: DateFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  ): string {
    if (!dateStr) return '-'

    const date = typeof dateStr === 'string'
      ? new Date(dateStr + 'T00:00:00')
      : dateStr

    return date.toLocaleDateString(browserLocale.value, options)
  }

  /**
   * Format a date to month and year only (e.g., "April 2026" or "เมษายน 2026")
   */
  function formatMonthYear(
    date: Date,
    options: Pick<DateFormatOptions, 'month' | 'year'> = {
      month: 'long',
      year: 'numeric'
    }
  ): string {
    return date.toLocaleDateString(browserLocale.value, options)
  }

  /**
   * Format a date to short weekday name (e.g., "Mon" or "จ")
   */
  function formatShortWeekday(date: Date): string {
    return date.toLocaleDateString(browserLocale.value, { weekday: 'short' })
  }

  /**
   * Format a date to long weekday name (e.g., "Monday" or "วันจันทร์")
   */
  function formatLongWeekday(date: Date): string {
    return date.toLocaleDateString(browserLocale.value, { weekday: 'long' })
  }

  /**
   * Get array of short weekday names for calendar headers
   * Returns Sunday-first array based on current locale
   */
  function getWeekdayShortNames(): string[] {
    // Generate weekday names from a known Sunday date
    const baseDate = new Date(2024, 0, 7) // Jan 7, 2024 is a Sunday
    const weekdays: string[] = []

    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate)
      date.setDate(baseDate.getDate() + i)
      weekdays.push(formatShortWeekday(date))
    }

    return weekdays
  }

  return {
    browserLocale,
    formatDate,
    formatMonthYear,
    formatShortWeekday,
    formatLongWeekday,
    getWeekdayShortNames
  }
}
